import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../services/firebase';
import { ref, get, update } from 'firebase/database';

const Skills = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [customSkill, setCustomSkill] = useState('');

  const skillCategories = {
    'Programming Languages': [
      'JavaScript', 'Python', 'Java', 'C++', 'Ruby', 'PHP', 'Swift', 'TypeScript',
      'Go', 'Rust', 'Kotlin', 'C#'
    ],
    'Web Technologies': [
      'React', 'Angular', 'Vue.js', 'Node.js', 'Express.js', 'Django', 'Flask',
      'HTML5', 'CSS3', 'Bootstrap', 'Tailwind CSS', 'jQuery'
    ],
    'Database': [
      'MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'Oracle', 'Firebase',
      'Microsoft SQL Server', 'Cassandra'
    ],
    'Cloud Platforms': [
      'AWS', 'Azure', 'Google Cloud', 'Heroku', 'DigitalOcean', 'Firebase'
    ],
    'DevOps & Tools': [
      'Git', 'Docker', 'Kubernetes', 'Jenkins', 'JIRA', 'Confluence',
      'Travis CI', 'CircleCI', 'Linux', 'Bash'
    ],
    'Mobile Development': [
      'React Native', 'Flutter', 'iOS', 'Android', 'Xamarin', 'Ionic'
    ],
    'AI & Machine Learning': [
      'TensorFlow', 'PyTorch', 'Scikit-learn', 'OpenCV', 'NLP', 'Computer Vision'
    ],
    'Soft Skills': [
      'Team Leadership', 'Project Management', 'Communication', 'Problem Solving',
      'Time Management', 'Agile Methodology'
    ]
  };

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const profileStep = localStorage.getItem('profileStep');
    
    if (!userId) {
      navigate('/login');
      return;
    }

    const initializeSkills = async () => {
      try {
        const userRef = ref(database, `users/${userId}`);
        const snapshot = await get(userRef);
        
        if (snapshot.exists()) {
          const userData = snapshot.val();
          if (!profileStep || profileStep < '4' || userData.profileStep < 4) {
            navigate('/profile/education');
            return;
          }
          
          if (userData.skills && userData.skills.length > 0) {
            setSelectedSkills(userData.skills);
          }
        }
      } catch (error) {
        console.error('Error:', error);
        setError('Failed to load skills data');
      } finally {
        setLoading(false);
      }
    };

    initializeSkills();
  }, [navigate]);

  const handleSkillToggle = (skill) => {
    setSelectedSkills(prev => 
      prev.includes(skill)
        ? prev.filter(s => s !== skill)
        : [...prev, skill]
    );
  };

  const handleAddCustomSkill = () => {
    if (customSkill && !selectedSkills.includes(customSkill)) {
      setSelectedSkills(prev => [...prev, customSkill]);
      setCustomSkill('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        throw new Error('User not found');
      }

      const userRef = ref(database, `users/${userId}`);
      const updates = {
        skills: selectedSkills,
        profileStep: 5,
        profileCompleted: true,
        updatedAt: new Date().toISOString()
      };

      await update(userRef, updates);
      localStorage.setItem('profileStep', '5');
      navigate('/dashboard/user');
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to update skills');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fe]">
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200">
        <div className="h-full w-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300"></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            What are your skills?
          </h2>
          {/* Progress steps similar to Education.jsx */}
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <form onSubmit={handleSubmit} className="p-8">
            {/* Custom Skill Input */}
            <div className="mb-8">
              <div className="flex gap-4">
                <input
                  type="text"
                  value={customSkill}
                  onChange={(e) => setCustomSkill(e.target.value)}
                  placeholder="Add a custom skill"
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={handleAddCustomSkill}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
                >
                  Add Skill
                </button>
              </div>
            </div>

            {/* Skill Categories */}
            {Object.entries(skillCategories).map(([category, skills]) => (
              <div key={category} className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{category}</h3>
                <div className="flex flex-wrap gap-3">
                  {skills.map(skill => (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => handleSkillToggle(skill)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                        selectedSkills.includes(skill)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Selected Skills Summary */}
            <div className="mt-8 p-6 bg-gray-50 rounded-xl">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Selected Skills ({selectedSkills.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {selectedSkills.map(skill => (
                  <span
                    key={skill}
                    className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={loading || selectedSkills.length === 0}
                className="px-8 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Complete Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Skills;